import { useCallback, useSyncExternalStore } from "react";

/**
 * The in-app "Reduce motion" preference.
 *
 * Deliberately built as a small parallel of the theme system rather than as
 * anything new: a value in `localStorage`, a class on `<html>`, and a snippet
 * in index.html that applies the class before the first paint. The OS-level
 * `prefers-reduced-motion` setting is untouched and still drives its own
 * (separate, narrower) rules in index.css — this is the product's own set,
 * and the two compose: either one being on reduces motion.
 *
 * NOT stored per account. Persisting it on `profiles` means a new column,
 * which means regenerating the client's schema types through the Docker-only
 * `make gen` — out of scope here. The consequence is the honest one: the
 * preference is per browser, like the theme, and follows nobody to a second
 * device. See the follow-up note in the Profile page's Preferences card.
 */

/** Storage key — keep in sync with the pre-paint snippet in index.html. */
const STORAGE_KEY = "reduce-motion";
/** The class the snippet and this module toggle on `<html>`. */
const HTML_CLASS = "reduce-motion";

const listeners = new Set<() => void>();

/**
 * Reading storage can throw outright in private mode / with storage disabled,
 * which is exactly the case the theme snippet swallows too. A session that
 * cannot remember the preference is a session without it, not a crash.
 */
const readStored = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const applyClass = (on: boolean) => {
  document.documentElement.classList.toggle(HTML_CLASS, on);
};

const subscribe = (onStoreChange: () => void) => {
  listeners.add(onStoreChange);
  // A second tab flipping the switch writes the key; the class on THIS
  // document is ours to bring back in line.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    applyClass(readStored());
    onStoreChange();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener("storage", onStorage);
  };
};

const setPref = (on: boolean) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(on));
  } catch {
    // Storage disabled: the class still applies, so the preference holds for
    // this page's life and is simply forgotten on the next load.
  }
  applyClass(on);
  for (const listener of listeners) listener();
};

/**
 * Is motion reduced right now, by either route — the OS setting or the in-app
 * toggle? For the handful of animations that are driven from JavaScript rather
 * than CSS (the backlog's collapse cascade, the hero's cursor-chased glow),
 * where a class on `<html>` cannot reach.
 *
 * Read at the moment of the gesture rather than subscribed to, so a flip of
 * the switch takes effect on the very next click with nothing to re-render.
 */
export const motionIsReduced = (): boolean => {
  if (typeof window === "undefined") return false;
  if (readStored()) return true;
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

/**
 * The preference as state, for the control that sets it. Returns the stored
 * value only — never the OS setting — because the switch must show what the
 * user chose here, not what their desktop happens to say.
 */
export const useReducedMotionPref = (): [boolean, (on: boolean) => void] => {
  const enabled = useSyncExternalStore(
    subscribe,
    readStored,
    // No DOM to read on the server; the snippet is what resolves it in the
    // browser, before React ever runs.
    () => false,
  );
  return [enabled, useCallback((on: boolean) => setPref(on), [])];
};
